import type { IDBPDatabase } from "idb";
import type { Session } from "../../types/core.js";
import type { MonologDB } from "../indexeddb-schema.js";

type WithDbOperation = <T>(
	onUnavailableMessage: string,
	fallback: () => T | Promise<T>,
	operation: (db: IDBPDatabase<MonologDB>) => Promise<T>,
) => Promise<T>;

export class SessionRepository {
	constructor(private readonly withDb: WithDbOperation) {}

	async saveSession(session: Session): Promise<void> {
		return this.withDb(
			"Database not available, skipping session save",
			() => undefined,
			async (db) => {
				await db.put("sessions", session);
			},
		);
	}

	async getSession(sessionId: string): Promise<Session | undefined> {
		return this.withDb(
			"Database not available, returning undefined for session",
			() => undefined,
			async (db) => db.get("sessions", sessionId),
		);
	}

	async getSessionsByUser(userId: string): Promise<Session[]> {
		return this.withDb(
			"Database not available, returning empty sessions array",
			() => [],
			async (db) => db.getAllFromIndex("sessions", "by-user", userId),
		);
	}

	async updateSessionMetadata(
		sessionId: string,
		metadata: { title?: string; isFavorite?: boolean },
	): Promise<void> {
		return this.withDb(
			"Database not available, skipping session metadata update",
			() => undefined,
			async (db) => {
				const tx = db.transaction("sessions", "readwrite");
				const store = tx.objectStore("sessions");
				const session = await store.get(sessionId);

				if (!session) {
					console.warn(`Session ${sessionId} not found`);
					return;
				}

				if (metadata.title !== undefined) {
					session.title = metadata.title;
				}

				if (metadata.isFavorite !== undefined) {
					session.isFavorite = metadata.isFavorite;
				}

				await store.put(session);
				await tx.done;
			},
		);
	}

	async updateSessionTranscript(
		sessionId: string,
		transcript: Session["transcript"],
	): Promise<void> {
		return this.withDb(
			"Database not available, skipping transcript update",
			() => undefined,
			async (db) => {
				const tx = db.transaction("sessions", "readwrite");
				const store = tx.objectStore("sessions");
				const session = await store.get(sessionId);

				if (!session) {
					console.warn(`Session ${sessionId} not found`);
					return;
				}

				session.transcript = transcript;
				await store.put(session);
				await tx.done;
			},
		);
	}

	async deleteSession(sessionId: string): Promise<void> {
		return this.withDb(
			"Database not available, skipping session delete",
			() => undefined,
			async (db) => {
				const tx = db.transaction(
					["sessions", "audioFiles", "summaries"],
					"readwrite",
				);

				await tx.objectStore("sessions").delete(sessionId);

				const audioStore = tx.objectStore("audioFiles");
				const audioFiles = await audioStore
					.index("by-session")
					.getAllKeys(sessionId);
				for (const key of audioFiles) {
					await audioStore.delete(key);
				}

				await tx.objectStore("summaries").delete(sessionId);
				await tx.done;
			},
		);
	}

	async deleteSessions(sessionIds: string[]): Promise<void> {
		if (sessionIds.length === 0) {
			return;
		}

		return this.withDb(
			"Database not available, skipping bulk session delete",
			() => undefined,
			async (db) => {
				const tx = db.transaction(
					["sessions", "audioFiles", "summaries"],
					"readwrite",
				);

				const sessionStore = tx.objectStore("sessions");
				const audioStore = tx.objectStore("audioFiles");
				const summaryStore = tx.objectStore("summaries");

				for (const sessionId of sessionIds) {
					await sessionStore.delete(sessionId);

					const audioFiles = await audioStore
						.index("by-session")
						.getAllKeys(sessionId);
					for (const key of audioFiles) {
						await audioStore.delete(key);
					}

					await summaryStore.delete(sessionId);
				}

				await tx.done;
			},
		);
	}

	async getAllSessions(): Promise<Session[]> {
		return this.withDb(
			"Database not available, returning empty array",
			() => [],
			async (db) => db.getAll("sessions"),
		);
	}
}
